import React, { useMemo } from 'react';

import AsyncSelect from 'react-select/async';
import { debounce } from 'lodash';

import { get } from 'helpers/api';

import type { NewTag, Tag } from 'bundles/crm/tags/types';

interface Props {
  value: Tag[];
  onValueChange: (tags: (Tag | NewTag)[]) => void;
}

interface Option {
  label: string;
  value: number | string;
}

export default function CrmTagsInput({ value, onValueChange }: Props) {
  const debouncedLoadOptions = useMemo(() => {
    return debounce((label, callback) => {
      loadOptions(label, value).then(callback);
    }, 300);
  }, [value]);

  return (
    <AsyncSelect
      components={{ DropdownIndicator: null }}
      isMulti
      loadOptions={debouncedLoadOptions}
      placeholder={false}
      value={toOptions(value)}
      onChange={options => onValueChange(toTags(options))}
    />
  );
}

async function loadOptions(label: string, selectedTags: Tag[]) {
  const query = label.trim().toLowerCase();

  const tags = await get('/crm/tags', { params: { query, visibility: 'shared' } });

  // Ensure unpersisted tags are included to prevent case-sensitive double entries
  // AsyncSelect won't actually render these in the dropdown, which is perfect
  for (const tag of (selectedTags || [])) {
    tags.push(tag);
  }

  const options: Option[] = toOptions(tags);

  if (query) {
    const exactMatch = tags.find((tag: Tag) => tag.label.toLowerCase() === query);
    if (!exactMatch) {
      options.push({ label: `${label} (Create new tag)`, value: label });
    }
  }

  return options;
}

function toOptions(tags: Tag[]) {
  return (tags || []).map(toOption);
}

function toOption(tag: Tag): Option {
  return { label: tag.label, value: tag.id || tag.label };
}

function toTags(options: readonly Option[]) {
  return (options || []).map(toTag);
} 

function toTag(option: Option): Tag | NewTag {
  if (typeof option.value === 'number') {
    return {
      id: option.value,
      label: option.label,
    };
  } else {
    return {
      label: option.value,
      visibility: 'shared',
    };
  }
}
